/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */

import Colors from './Colors';

export type AssetNote = {
  createdOn: string,
  description: string,
  id: string,
  name: string,
  title: string,
  updatedOn: string,
}

export enum TicketStatus {
  REPORTED = 'reported',
  INPROGRESS = 'inprogress',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  REOPENED = 're-open'
}

export enum NotificationType {
  RAISED = 'TICKET_RAISED',
  UPDATE = 'TICKET_UPDATED',
  INSIGHT = 'INSIGHT',
  ANOMALY = 'ANOMALY',
  ALERT = 'ALERT',
}

export enum NotificationSeverity {
  CRITICAL = 1,
  HIGH = 2,
  MODERATE = 3,
  LOW = 4,
}

export enum HappyScoreCategory {
  INTERNAL_ENVIRONMENT = 'INTERNAL_ENVIRONMENT',
  EXTERNAL_ENVIRONMENT = 'EXTERNAL_ENVIRONMENT',
  ENERGY = 'ENERGY',
  OCCUPANCY = 'OCCUPANCY',
  AMENITIES = 'AMENITIES',
  ERGONOMICS = 'ERGONOMICS',
  SATISFACTION = 'SATISFACTION',
  IT = 'IT',
  HAPPY_SCORE = 'HAPPY_SCORE',
}

export const HappyScoreCategoryTitles = {
  [HappyScoreCategory.INTERNAL_ENVIRONMENT]: 'Internal Environment',
  [HappyScoreCategory.EXTERNAL_ENVIRONMENT]: 'External Environment',
  [HappyScoreCategory.ENERGY]: 'Energy & Waste',
  [HappyScoreCategory.OCCUPANCY]: 'Building Occupancy',
  [HappyScoreCategory.AMENITIES]: 'Amenities',
  [HappyScoreCategory.ERGONOMICS]: 'Ergonomics',
  [HappyScoreCategory.SATISFACTION]: 'Occupant Satisfaction',
  [HappyScoreCategory.IT]: 'IT',
  [HappyScoreCategory.HAPPY_SCORE]: 'Building Happy score',
};

export enum ActivitySection { ALL = 'ALL', REPORTER = 'REPORTER', COMMENTS = 'INTERNAL', HISTORY = 'HISTORY' }

export enum TicketPriority {
  CRITICAL = 0,
  HIGH = 1,
  MODERATE = 2,
  LOW = 3,
  OTHER = 4,
}

export const DEFAULT_TICKET_PRIORITY: TicketPriority = TicketPriority.HIGH;

export const ticketPriorityIsValid = (priority: TicketPriority) => Object.values(TicketPriority)
  .filter(Number.isInteger).includes(priority);

export type Ticket = {
  id: string;
  createdOn: string;
  floorName: string;
  floorId?: string;
  areaId?: string;
  spaceId?: string;
  locationId: string;
  locationName: string;
  assetId: string;
  assetName: string;
  assetTemplateId: string;
  assetTemplateName: string;
  name: string;
  priority: TicketPriority;
  slaHours: number;
  slaMinutes: number;
  reporterName?: string;
  reporterEmail?: string;
  reporter?: string;
  status: TicketStatus;
  initialStartedOn: string | null;
  images: {
    filename: string;
    createdOn: string;
    createdBy: string | null;
  }[];
  buildingId: string;
  comments: any[];
  history: {
    text: string;
    createdOn: string;
  }[];
  completedOn: string;
  startedOn: string;
  assignee: string;
  dueDate: string;
  asset?: object;
  issueDescription?: string;
  responseDueDate?: string;
  isResponseOverdue?: boolean;
  onMap?: boolean;
  hasXY?: boolean;
  pinned?: boolean;
  userGroupIds?: string[];
}

export type TemplateField = {
  Name: string,
  Format: string,
  IsMandatory: boolean,
};

export enum TemplateIssueCategory {
  CLEANING = 'CLEANING',
  BIN_WASTE = 'BIN_WASTE',
  CATERING_CONCIERGE = 'CATERING_CONCIERGE',
  MAINTENANCE_GENERAL = 'MAINTENANCE_GENERAL',
  PANTRY_TEA_STATION = 'PANTRY_TEA_STATION',
  RECEPTION_CONCIERGE = 'RECEPTION_CONCIERGE',
  IT = 'IT',
  AV = 'AV',
}

export const TemplateIssueCategoryTitles = {
  [TemplateIssueCategory.CLEANING]: 'Cleaning',
  [TemplateIssueCategory.BIN_WASTE]: 'Bin waste',
  [TemplateIssueCategory.CATERING_CONCIERGE]: 'Catering concierge',
  [TemplateIssueCategory.MAINTENANCE_GENERAL]: 'Maintenance general',
  [TemplateIssueCategory.PANTRY_TEA_STATION]: 'Pantry tea station',
  [TemplateIssueCategory.RECEPTION_CONCIERGE]: 'Reception concierge',
  [TemplateIssueCategory.IT]: 'IT',
  [TemplateIssueCategory.AV]: 'AV',
};

export type TemplateIssue = {
  Id?: string,
  LocalId?: string,
  Text: string,
  UserGroupId?: string[],
  Priority?: TicketPriority,
  Enabled?: boolean,
  PrevUserGroupId?: string[],
  Category?: TemplateIssueCategory,
}

export enum TemplateType {
  ASSET = 'ASSET',
  SENSOR = 'SENSOR',
  METER = 'METER',
}

export const TemplateTypeTitles = {
  [TemplateType.ASSET]: 'Asset',
  [TemplateType.METER]: 'Meter',
  [TemplateType.SENSOR]: 'Sensor',
};

export enum SensorLabel {
  MTS = 'MTS',
  PCS = 'PCS',
  LS = 'LS',
  ENT = 'ENT',
  TPS = 'TPS',
}

export enum MeterLabel {
  EM = 'EM',
  HM = 'HM',
  WM = 'WM',
  GM = 'GM',
}

export const AssetLabels = {
  [TemplateType.SENSOR]: Object.values(SensorLabel),
  [TemplateType.METER]: Object.values(MeterLabel),
};

export const AssetTypeTitles = {
  [SensorLabel.MTS]: 'IAQ multisensor',
  [SensorLabel.PCS]: 'Vergesense occupancy - people counting sensor',
  [SensorLabel.LS]: 'Lighting sensor - PIR',
  [SensorLabel.ENT]: 'Entry sensor',
  [SensorLabel.TPS]: 'Temperature and pressure sensor',
  [MeterLabel.EM]: 'Electric meter',
  [MeterLabel.HM]: 'Energy/heat meter',
  [MeterLabel.WM]: 'Water meter',
  [MeterLabel.GM]: 'Gas meter',
};

export type AssetCustomField = {
  name: string,
  value: string,
  format?: string,
};

export type Template = {
  name: string,
  id: string,
  issues: TemplateIssue[],
  customfields: TemplateField[],
  type?: TemplateType,
  ppmUrl?: string,
  title?: string,
  subtitle?: string,
  subtitleUrl?: string,
  CreatedOn?: string,
  ModifiedOn?: string,
  disablePhotoUpload?: boolean,
}

export interface AssetInstance {
  name: string,
  description: string,
  manufacturer: string,
  model: string,
  serial: string,
  locationId: string,
  customFields: AssetCustomField[],
  labels?: SensorLabel[] | MeterLabel[] | null,
  id: string,
  imageUrl: string,
  thumbnailUrl: string,
  clientId: string,
  createdOn: string,
  updatedOn: string,
  templateName: string,
  locationBuilding: string | null,
  locationFloor: string | null,
  locationArea: string | null,
  locationSpace: string | null,
  notes?: any[],
  type?: TemplateType,
  templateId: string,
  buildingId: string,
  template: Template,
  solveTagIds?: string[],
  lastDevicePayload?: {
    data?: {
      points?: {
        [metricName: string]: {
          status?: string,
          present_value?: number | string | null,
        },
      },
      version?: string,
      timestamp?: string,
    },
  },
}

export type TemplateGroup = {
  Name: string,
  ButtonPrototypeGroupId: string,
  buttonPrototypes: Template[],
}

export enum UserStatus {
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  VERIFIED = 'VERIFIED',
}

export interface UserNotificationPreferences {
  enabledCategories: {
    [K in keyof typeof HappyScoreCategory]: boolean;
  }
}

export interface UserInstance {
  id: string;
  clientId: string;
  email: string;
  createdAt: string;
  lastName?: string;
  firstName?: string;
  lastAuthenticatedAt?: string;
  numberOfAuthenticationsEver: number;
  updatedAt: string;
  userGroupIds: string[];
  roles: string[];
  status: UserStatus;
  lastViewedNotificationCentre: string;
  notificationPreferences: UserNotificationPreferences;
}

export type UserGroup = {
  Name: string,
  UserGroupId: string,
  Description: string,
  UsersCount?: number,
}

export enum Metric {
  OCCUPANCY = 'zone_occupancy_count',
  UTILIZATION = 'zone_utilization',
  HOURS_BOOKED = 'zone_hours_booked',
  ELECTRICITY = 'processed_energy_accumulator',
  WATER = 'processed_water_volume_accumulator',
  GAS = 'processed_gas_volume_accumulator',
  TEMPERATURE = 'zone_air_temperature_sensor',
  HUMIDITY = 'zone_air_relative_humidity_sensor',
  CO2 = 'zone_air_co2_concentration_sensor',
  TVOC = 'zone_air_voc_concentration_sensor',
  FORMALDEHYDE = 'zone_air_formaldehyde_concentration_sensor',
  PM1 = 'zone_air_pm1pt0_density_sensor',
  PM25 = 'zone_air_pm2pt5_density_sensor',
  PM4 = 'zone_air_pm4pt0_density_sensor',
  PM10 = 'zone_air_pm10pt0_density_sensor',
  WATER_VAPOUR = 'zone_air_water_vapor_density_sensor',
  AIR_PRESSURE = 'zone_air_static_pressure_sensor',
  NOISE = 'soundpressurelevel_sensor',
  LIGHT = 'illuminance_sensor',
  AQI = 'zone_air_aqi_sensor'
}

export enum UserPermission {
  TICKETS_CREATE = 'TICKETS_CREATE',
  TICKETS_READ = 'TICKETS_READ',
  TICKETS_UPDATE = 'TICKETS_UPDATE',
  TICKETS_DELETE = 'TICKETS_DELETE',

  TEMPLATES_CREATE = 'TEMPLATES_CREATE',
  TEMPLATES_READ = 'TEMPLATES_READ',
  TEMPLATES_UPDATE = 'TEMPLATES_UPDATE',
  TEMPLATES_DELETE = 'TEMPLATES_DELETE',

  ASSETS_CREATE = 'ASSETS_CREATE',
  ASSETS_READ = 'ASSETS_READ',
  ASSETS_UPDATE = 'ASSETS_UPDATE',
  ASSETS_DELETE = 'ASSETS_DELETE',

  USER_GROUPS_CREATE = 'USER_GROUPS_CREATE',
  USER_GROUPS_READ = 'USER_GROUPS_READ',
  USER_GROUPS_UPDATE = 'USER_GROUPS_UPDATE',
  USER_GROUPS_DELETE = 'USER_GROUPS_DELETE',

  USERS_CREATE = 'USERS_CREATE',
  USERS_READ = 'USERS_READ',
  USERS_UPDATE = 'USERS_UPDATE',
  USERS_DELETE = 'USERS_DELETE',

  LOCATIONS_CREATE = 'LOCATIONS_CREATE',
  LOCATIONS_READ = 'LOCATIONS_READ',
  LOCATIONS_UPDATE = 'LOCATIONS_UPDATE',
  LOCATIONS_DELETE = 'LOCATIONS_DELETE',

  ROLES_CREATE = 'ROLES_CREATE',
  ROLES_UPDATE = 'ROLES_UPDATE',
  ROLES_READ = 'ROLES_READ',
}

export enum LocationType {
  BUILDING = 'Building',
  FLOOR = 'Floor',
  AREA = 'Area',
  SPACE = 'Space',
}

export type TagColor = 'blue' | 'orange' | 'grey' | 'yellow' | 'green' | 'pink';

export const TAG_COLORS: Record<
  TagColor,
  { text: string; background: string }
> = {
  blue: {
    text: Colors.light.hereworksBlue600,
    background: Colors.light.hereworksBlue50,
  },
  orange: {
    text: Colors.light.hereworksOrange500,
    background: Colors.light.hereworksOrange50,
  },
  grey: {
    text: Colors.light.hereworksBlack400,
    background: Colors.light.hereworksBlue50,
  },
  yellow: {
    text: Colors.light.hereworksYellow800,
    background: Colors.light.hereworksYellow50,
  },
  green: {
    text: Colors.light.hereworksGreen600,
    background: Colors.light.hereworksGreen50,
  },
  pink: {
    text: Colors.light.hereworksPink,
    background: Colors.light.hereworksRed50,
  },
};
